import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Label, Input, Button, Modal, ModalFooter, ModalHeader, ModalBody } from 'reactstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Moment from 'react-moment';
import 'moment-timezone';

import { getApiToken } from '../msalInstance';

function EditStaticValues(props) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    function closeModal() {
        setData(null);
        props.setModalItemID(null);
        props.setShowModal(false);
    }

    async function safeJson(response) {
        if (response.status === 204) return null;
        const text = await response.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }

    async function HandleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const accessToken = await getApiToken();

            const submitOptions = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    internalValue: e.target.internal_value.value,
                    externalValue: e.target.external_value.value,
                    notes: e.target.notes.value,
                    modifiedBy: props.username,
                    sortOrder: e.target.sortOrder.value,
                    name: e.target.displayName.value
                })
            };

            const res = await fetch(`/api/StaticValue/${props.itemID}`, submitOptions);
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

            // PUT returns 204 No Content — don't call res.json()
            // Close and refresh list
            closeModal();
            if (typeof props.getStaticValues === 'function') {
                await props.getStaticValues();
            }
        } catch (err) {
            console.error('Update failed:', err);
            setError(err.message ?? String(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        let cancelled = false;

        async function loadItem(id) {
            setLoading(true);
            setError(null);

            try {
                const accessToken = await getApiToken();
                const res = await fetch(`/api/StaticValue/${id}`, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });

                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

                const item = await safeJson(res); // won’t throw on empty body
                if (!cancelled) {
                    if (!item) throw new Error('Empty response body.');
                    setData(item);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Load failed:', err);
                    setError(err.message ?? String(err));
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (props.showModal && props.itemID) {
            loadItem(props.itemID);
        } else {
            // reset when modal closed or id cleared
            setData(null);
            setError(null);
            setLoading(false);
        }

        return () => {
            cancelled = true;
        };
    }, [props.showModal, props.itemID]);

    return (
        <Modal isOpen={props.showModal}>
            {loading && <div>Loading...</div>}
            {error && <div>{`There is a problem fetching the post data - ${error}`}</div>}
            {data && !loading && !error && (
                <>
                    <ModalHeader>{data.name}</ModalHeader>
                    <ModalBody>
                        <Container>
                            <Form onSubmit={HandleSubmit}>
                                <Row className="mb-2">
                                    <Col xs="4">
                                        <Label htmlFor="displayName">Display Name</Label>
                                    </Col>
                                    <Col xs="8">
                                        <Input required type="text" name="displayName" defaultValue={data.name} />
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs="4">
                                        <Label htmlFor="internal_value">Internal Value</Label>
                                    </Col>
                                    <Col xs="8">
                                        <Input step=".01" required type="number" name="internal_value" defaultValue={data.internalValue} />
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs="4">
                                        <Label htmlFor="external_value">External Value</Label>
                                    </Col>
                                    <Col xs="8">
                                        <Input step=".01" required type="number" name="external_value" defaultValue={data.externalValue} />
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xs="4">
                                        <Label htmlFor="notes">Notes</Label>
                                    </Col>
                                    <Col xs="8">
                                        <Input type="textarea" name="notes" defaultValue={data.notes} />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs="4">
                                        <Label htmlFor="sortOrder">Sort Order</Label>
                                    </Col>
                                    <Col xs="8">
                                        <Input step="1" type="number" required name="sortOrder" defaultValue={data.sortOrder} />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs="12">
                                        <div className="modifiedByText">
                                            Last modified by {data.modifiedBy} on{' '}
                                            <Moment local format="MM/DD/YY hh:mm a">
                                                {data.modified}
                                            </Moment>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <ModalFooter>
                                        <Button onClick={closeModal}>Cancel</Button>
                                        <Button color="primary" type="submit" disabled={loading}>
                                            Submit
                                        </Button>
                                    </ModalFooter>
                                </Row>
                            </Form>
                        </Container>
                    </ModalBody>
                </>
            )}
        </Modal>
    );
}

export default EditStaticValues;
